import { MktButton, SectionHeader, Tag } from "./primitives";
import { useT } from "../../i18n";
import type { OwnerCapabilities } from "../../lib/marketplace-api";
import type { OwnerAction } from "./owner-console-logic";

const ACTION_KEYS: { key: OwnerAction; labelKey: string; descKey: string }[] = [
  { key: "release", labelKey: "marketplace.owner.releaseLabel", descKey: "marketplace.owner.releaseDesc" },
  { key: "relock", labelKey: "marketplace.owner.relockLabel", descKey: "marketplace.owner.relockDesc" },
  { key: "private_sale", labelKey: "marketplace.owner.privateSaleLabel", descKey: "marketplace.owner.privateSaleDesc" },
];

export function OwnerActionsPanel({
  capabilities,
  onOpen,
}: {
  capabilities: OwnerCapabilities;
  onOpen: (action: OwnerAction) => void;
}) {
  const t = useT();
  return (
    <section data-testid="owner-actions-panel">
      <SectionHeader
        index="02"
        title={t("marketplace.owner.actionsTitle")}
        action={
          <Tag tone="muted" testId="owner-actions-pricing">
            {capabilities.pricing_law.tiers.join(" · ")} · {t("marketplace.owner.gridZeroManual")}
          </Tag>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ACTION_KEYS.map((m) => {
          const cap = capabilities.actions[m.key];
          const available = !!cap?.available;
          const label = t(m.labelKey);
          return (
            <div
              key={m.key}
              data-testid={`owner-action-${m.key}`}
              data-available={available ? "true" : "false"}
              className="neon-glass rounded-xl border border-border-divider p-5 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-base text-text-primary">{label}</span>
                {!available && (
                  <Tag tone="coral" testId={`owner-action-${m.key}-failclosed`}>
                    {t("marketplace.owner.failClosed")}
                  </Tag>
                )}
              </div>
              <p className="font-mono text-[11px] leading-relaxed text-text-muted flex-1">{t(m.descKey)}</p>
              <MktButton
                variant={available ? "primary" : "ghost"}
                disabled={!available}
                testId={`owner-action-${m.key}-btn`}
                onClick={() => onOpen(m.key)}
              >
                {available ? label : t("marketplace.owner.unavailable")}
              </MktButton>
              {!available && (
                <p data-testid={`owner-action-${m.key}-reason`} className="font-mono text-[10px] text-neon-coral/80">
                  {cap?.reason ?? t("marketplace.owner.unavailable")}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
